import { get, keyBy } from 'lodash'

import { getSelectedPerformanceBand } from '@edulastic/constants/reportUtils/common'
import {
  ANALYZE_BY_OPTIONS,
  getChartData,
} from '@edulastic/constants/reportUtils/dataWarehouseReports/multipleAssessmentReport'
import { EXTERNAL_SCORE_TYPES } from '../common/utils'

/**
 * NOTE: the constants declared here affects only the Multiple Assessment Report in Data Warehouse
 */

export const ANALYZE_BY_AVERAGE_SCORE_TABLE_PAGE_SIZE = 100
export const ANALYZE_BY_PERFORMANCE_DISTRIBUTION_TABLE_PAGE_SIZE = 25

export const CHART_TOP_LABEL_ROW_HEIGHT = 50

const TOP_SCORE_ROW_LABELS = {
  [EXTERNAL_SCORE_TYPES.SCALED_SCORE]: 'SCALED SCORE',
  [EXTERNAL_SCORE_TYPES.LEXILE_SCORE]: 'LEXILE SCORE',
  [EXTERNAL_SCORE_TYPES.QUANTILE_SCORE]: 'QUANTILE SCORE',
  [EXTERNAL_SCORE_TYPES.PERCENTILE]: 'PERCENTILE',
}

export const INITIAL_FILTERS = {
  reportId: '',
  termId: '',
  districtIds: '',
  networkIds: '',
  schoolIds: '',
  teacherIds: '',
  grades: '',
  subjects: '',
  courseId: 'All',
  classIds: '',
  groupIds: '',
  assignedBy: 'anyone',
  testTermIds: '',
  assessmentTypes: '',
  testGrades: '',
  testSubjects: '',
  tagIds: '',
  testUniqIds: '',
  profileId: '',
  externalScoreType: EXTERNAL_SCORE_TYPES.SCALED_SCORE,
  race: 'all',
  gender: 'all',
  iepStatus: 'all',
  frlStatus: 'all',
  ellStatus: 'all',
  hispanicEthnicity: 'all',
}

export const REQUEST_FILTERS = {
  reportId: '',
  termId: '',
  districtIds: '',
  networkIds: '',
  schoolIds: '',
  teacherIds: '',
  subjects: '',
  grades: '',
  courseId: '',
  classIds: '',
  groupIds: '',
  testTermIds: '',
  testUniqIds: '',
  testSubjects: '',
  testGrades: '',
  assessmentTypes: '',
  tagIds: '',
  profileId: '',
  race: 'all',
  gender: 'all',
  iepStatus: 'all',
  frlStatus: 'all',
  ellStatus: 'all',
  hispanicEthnicity: 'all',
  externalScoreType: EXTERNAL_SCORE_TYPES.SCALED_SCORE,
  analyzeBy: ANALYZE_BY_OPTIONS[0].key,
}

export const getTopLabelsLayout = (
  rowCount,
  rowHeight = CHART_TOP_LABEL_ROW_HEIGHT
) => ({
  rowCenterYs: Array.from(
    { length: rowCount },
    (_, index) => rowHeight * index + rowHeight / 2
  ),
  rowSeparatorYs: Array.from(
    { length: rowCount - 1 },
    (_, index) => rowHeight * (index + 1)
  ),
})

const getScoreRow = (key) => ({
  key,
  label: TOP_SCORE_ROW_LABELS[key],
  getValue: (row) => row?.topLabels?.[key] || '-',
})

/** Builds the rows to render above the chart for the current score-type filter. */
export const getChartTopLabelRows = (selectedKey) => {
  const scaledScoreRow = getScoreRow(EXTERNAL_SCORE_TYPES.SCALED_SCORE)
  if (selectedKey === EXTERNAL_SCORE_TYPES.SCALED_SCORE) {
    return [scaledScoreRow]
  }

  const externalScoreRow = getScoreRow(selectedKey)
  return [externalScoreRow, scaledScoreRow]
}

/**
 * Method to get specific data i.e. incompleteTests, selectedPerformanceBand and chartData.
 * @param {Record[]} filtersData - filter api response.
 * @param {Record} sharedReportFilters -  shared report filters data for the shared reports.
 * @param {Record} settings - popup filters data.
 * @param {Record[]} reportChartData - chart api response.
 * @returns {Record} containing incompleteTests, selectedPerformanceBand and chartData.
 */

export const getChartSpecifics = (
  filtersData,
  sharedReportFilters,
  settings,
  reportChartData,
  orgData,
  userTestTypesInternal
) => {
  const { terms = [], districtGroup = {} } = orgData
  const { sauTerms = [], stateTerms = [] } = districtGroup || {}
  const districtGroupTerms = [...sauTerms, ...stateTerms]
  const termsKeyedById = keyBy([...terms, ...districtGroupTerms], '_id')

  // performance band for chart should update post chart data API response
  const { bandInfo = [], externalBands = [] } = get(
    filtersData,
    'data.result',
    {}
  )

  const profileId = (sharedReportFilters || settings.requestFilters).profileId
  const selectedPerformanceBand = getSelectedPerformanceBand(
    bandInfo,
    profileId
  )

  const metricInfo = get(reportChartData, 'data.result', [])

  const chartData = getChartData(
    metricInfo,
    selectedPerformanceBand,
    externalBands,
    termsKeyedById,
    userTestTypesInternal
  )

  return {
    selectedPerformanceBand,
    chartData,
  }
}

/** Pick one external band definition (widest set) for stacked bar config. */
export const getExternalBandsFromChartData = (chartData = []) =>
  chartData.reduce((bands, cdItem) => {
    if (!cdItem.externalTestType || !cdItem.bands?.length) {
      return bands
    }
    return cdItem.bands.length > bands.length ? cdItem.bands : bands
  }, [])

export function buildChartData(
  chartData,
  barsDataForExternal,
  barsDataForInternal
) {
  return chartData.map((d) => {
    if (d.externalTestType) {
      const insideLabels = {}
      const bands = d.bands || []
      const barsCellDataForExternal = bands.reduce((res, band, index) => {
        const key = `bar${index + 1}`
        const barData = d.records.find((r) => r.id == band.id) || {}
        res[key] = barData.studentPerc
        insideLabels[key] = barData.insideLabels || {}
        return res
      }, {})
      const barsCellAdditionalDataForExternal = bands.reduce(
        (res, band, index) => {
          res[`bar${index + 1}`] = { fill: band.color }
          return res
        },
        {}
      )
      return {
        ...d,
        ...barsCellDataForExternal,
        insideLabels,
        additionalData: barsCellAdditionalDataForExternal,
      }
    }
    // for internal assessments
    const insideLabels = {}
    const barsCellDataForInternal = barsDataForInternal.reduce((res, ele) => {
      const barData = d.records.find((r) => r.threshold == ele.threshold) || {}
      res[ele.key] = barData.studentPerc
      insideLabels[ele.key] = barData.insideLabels || {}
      return res
    }, {})
    const barsCellAdditionalDataForInternal = barsDataForInternal.reduce(
      (res, ele) => {
        res[ele.key] = { fill: ele.fill }
        return res
      },
      {}
    )
    return {
      ...d,
      ...barsCellDataForInternal,
      insideLabels,
      additionalData: barsCellAdditionalDataForInternal,
    }
  })
}

export {
  ANALYZE_BY_KEYS,
  ANALYZE_BY_OPTIONS,
  augmentBandData,
  compareByMap,
  compareByOptionsInfo,
  findTestWithAverageBand,
  getDownloadCsvColumnHeadersFunc,
  getExternalScores,
  getTableData,
  getTestName,
  getWeightedAchievementLevel,
  groupByCompareByKey,
  sortKeys,
  tableColumnsData,
} from '@edulastic/constants/reportUtils/dataWarehouseReports/multipleAssessmentReport'
